import { api } from './api';
import type { Workspace } from '@/shared/stores/workspace.store';

/** Body for POST /workspaces ("Add brand", blueprint M20). */
export interface CreateWorkspaceInput {
  name: string;
  industry?: string;
  timezone?: string;
  language?: string;
  contentLang?: string;
  website?: string;
  /**
   * Agency (ADR-006): consent to one paid extra brand, sent after the
   * customer saw the price that a first try answered with EXTRA_BRAND_REQUIRED.
   */
  acceptExtraBrandCharge?: boolean;
  /** M-14: another brand of the account to copy the chosen settings from. */
  copyFrom?: string;
  include?: BrandCopySetting[];
}

/** What "Add brand" can copy from another brand (M-14). */
export type BrandCopySetting = 'voice' | 'hours' | 'policies' | 'occasions';
export const BRAND_COPY_SETTINGS: readonly BrandCopySetting[] = ['voice', 'hours', 'policies', 'occasions'];

/** DELETE /workspaces/:id answer. billingAdjusted false: the brand is gone, the Agency extra charge was not lowered yet. */
export interface DeleteWorkspaceResult {
  deleted: true;
  workspaceId: string;
  extraBrands: number | null;
  billingAdjusted: boolean;
}

export interface UpdateWorkspaceInput {
  name?: string;
  slug?: string;
  logoUrl?: string;
  timezone?: string;
  language?: string;
  contentLang?: string;
  website?: string;
  industry?: string;
}

/**
 * Mutations against the *current* workspace (resolved from the
 * X-Workspace-Id header attached by the request interceptor).
 *
 * Reads still live in useWorkspace() — that hook already returns the
 * full workspace object via the shared `/workspaces/me` query.
 */
export const workspaceService = {
  /**
   * Create another workspace owned by the signed-in user. The server answers
   * with the same shape as a `/workspaces/me` row (subscription and counts),
   * or 403 PLAN_REQUIRED when the owner's plan allows no more brands.
   */
  async create(input: CreateWorkspaceInput): Promise<Workspace> {
    // The add-brand sheet explains every plan refusal itself: no global toast.
    const res = await api.post<Workspace>('/workspaces', input, { handlesPlanErrors: true });
    return res.data;
  },

  /**
   * Deletes a brand and everything in it (ADR-006). Owner only; the brand's
   * name is typed back as confirmation. Refused with LAST_BRAND for the
   * account's only brand: deleting the account does that.
   */
  async remove(workspaceId: string, confirmName: string): Promise<DeleteWorkspaceResult> {
    // WorkspaceGuard checks the brand being deleted is the one in
    // X-Workspace-Id (400 WORKSPACE_MISMATCH otherwise), so send it explicitly
    // rather than trusting whichever brand the store has current.
    const res = await api.delete<DeleteWorkspaceResult>(`/workspaces/${encodeURIComponent(workspaceId)}`, {
      data: { confirmName },
      headers: { 'X-Workspace-Id': workspaceId },
    });
    return res.data ?? { deleted: true, workspaceId, extraBrands: null, billingAdjusted: true };
  },

  async updateCurrent(input: UpdateWorkspaceInput) {
    const res = await api.patch('/workspaces/me', input);
    return res.data;
  },

  /**
   * Asks a teammate (an admin or editor) to take the current brand over
   * (AL-20). Nothing moves until they accept; the answer names the plan the
   * brand will run on (theirs).
   */
  async transferOwnership(newOwnerId: string): Promise<TransferRequest> {
    const res = await api.post<TransferRequest>('/workspaces/me/transfer-ownership', { newOwnerId }, { handlesPlanErrors: true });
    if (!res.data) throw new Error('No transfer request in the answer');
    return res.data;
  },

  async cancelTransfer(): Promise<void> {
    await api.delete('/workspaces/me/transfer-ownership');
  },

  /**
   * The recipient's answer. Accepting moves the brand into their account
   * (ADR-006) and never charges anyone: into a full Agency it is refused
   * with 409 TARGET_AT_BRAND_LIMIT, and they add room in Billing first.
   */
  async answerTransfer(accept: boolean): Promise<void> {
    await api.post(`/workspaces/me/transfer-ownership/${accept ? 'accept' : 'decline'}`, undefined, { handlesPlanErrors: true });
  },
};

/** A transfer the owner asked for (AL-20). */
export interface TransferRequest {
  pendingOwnerId: string;
  requestedAt: string;
  expiresAt: string;
  recipientPlan: string;
  recipientName: string;
}
